/**
 * Page knobs (docs/composer-cards.md §13): the few typed, enumerated style
 * parameters each page archetype honors. Values are steps on the design
 * scale, never pixels. This one file is read by the server (validation,
 * read_page, the set_section_style mutation) and by the web renderer, so
 * the agent, the workspace and the pages can never disagree about what a
 * page can change.
 *
 * Adding a knob = one entry here + the renderer honoring it. A request the
 * catalog cannot serve is answered with the nearest thing and logged, so
 * this list grows from real asks.
 */

export type KnobValue = string;

export interface KnobDef {
  /** Key on `DocumentSection.style`. */
  knob: string;
  /** Rep-facing name, used in read_page and change lines. */
  label: string;
  /** Ordered: for stepped knobs, "bigger" / "smaller" move one index. */
  options: readonly string[];
  default: string;
  /** True when the options are an ordered scale (size, density); false for choices (placement). */
  stepped: boolean;
}

/** Per-section style overrides; only keys from `PAGE_KNOBS[section.type]` are valid. */
export type SectionStyle = Record<string, KnobValue>;

const SIZE = ['S', 'M', 'L', 'XL'] as const;

export const PAGE_KNOBS: Record<string, readonly KnobDef[]> = {
  cover: [
    { knob: 'logoScale', label: 'Logo size', options: SIZE, default: 'M', stepped: true },
    { knob: 'headlineSize', label: 'Headline size', options: SIZE, default: 'M', stepped: true },
    { knob: 'panelTint', label: 'Brand panel tint', options: ['light', 'brand', 'dark'], default: 'light', stepped: false },
  ],
  strategy: [
    { knob: 'statementSize', label: 'Statement size', options: SIZE, default: 'M', stepped: true },
    { knob: 'supportLine', label: 'Support line', options: ['on', 'off'], default: 'on', stepped: false },
    { knob: 'imagePlacement', label: 'Image placement', options: ['full', 'right', 'none'], default: 'full', stepped: false },
  ],
  product_plan: [
    { knob: 'density', label: 'Density', options: ['airy', 'normal', 'dense'], default: 'normal', stepped: true },
    { knob: 'headlineSize', label: 'Headline size', options: ['S', 'M', 'L'], default: 'M', stepped: true },
  ],
  // data_table: what a rep asks for after seeing a table is "tighter", "bigger
  // text", "lose the lines" — never a column change, which goes back through
  // build_table_page because it changes what the page CLAIMS. Density and
  // scale are safe to dial because src/domain/table-fit.ts already fit the
  // table at the default step; every option here is at or below that.
  //
  // Kept under the legacy key: custom_page's `table` layout returns this same
  // catalog (see knobsFor below), and a still-on-disk `data_table` section
  // (coerced to custom_page only in memory, src/state/store.ts) resolves
  // knobs against its stored type before that coercion runs in some callers.
  data_table: [
    { knob: 'density', label: 'Row density', options: ['airy', 'normal', 'dense'], default: 'normal', stepped: true },
    { knob: 'textSize', label: 'Table text size', options: ['S', 'M'], default: 'M', stepped: true },
    { knob: 'rules', label: 'Row lines', options: ['on', 'off'], default: 'on', stepped: false },
  ],
  // custom_page 'text' layout only (docs/custom-page.md §"Folding data_table
  // in" step 3). Media layouts (media_full/media_split) get their own catalog
  // below, and 'table' reuses data_table's — see knobsFor.
  // No 'L' on any custom_page headline: CUSTOM_PAGE_BUDGETS are measured at
  // M, and max-length text/media_split copy at L runs into the footer (15px
  // and 7px over on the real card). Same rule as data_table — every option
  // is at or below the size the content was fitted at.
  custom_page_text: [
    { knob: 'headlineSize', label: 'Headline size', options: ['S', 'M'], default: 'M', stepped: true },
  ],
  // custom_page 'media_full' / 'media_split' layouts only. `layout` toggles
  // between the two media arrangements (centered vs. split) — the set_section_style
  // mutation handler (src/state/mutations.ts) also mirrors a change to this
  // knob into `content.layout`, since the knob catalog itself depends on the
  // layout. `mediaSide` applies to media_split only, but is harmless (and
  // simpler to keep) on media_full too, where nothing reads it.
  custom_page_media: [
    { knob: 'layout', label: 'Layout', options: ['media_full', 'media_split'], default: 'media_full', stepped: false },
    { knob: 'mediaSide', label: 'Image side', options: ['left', 'right'], default: 'right', stepped: false },
    { knob: 'mediaFit', label: 'Image fit', options: ['fit', 'fill'], default: 'fit', stepped: false },
    { knob: 'headlineSize', label: 'Headline size', options: ['S', 'M'], default: 'M', stepped: true },
  ],
  section_divider: [
    { knob: 'titleSize', label: 'Title size', options: SIZE, default: 'M', stepped: true },
    { knob: 'imagePlacement', label: 'Image placement', options: ['right', 'full', 'none'], default: 'right', stepped: false },
  ],
  thank_you: [
    { knob: 'headlineSize', label: 'Headline size', options: SIZE, default: 'M', stepped: true },
    { knob: 'imagePlacement', label: 'Image placement', options: ['full', 'left', 'right', 'none'], default: 'full', stepped: false },
  ],
};

/**
 * `layout` is `custom_page` only (DocumentSectionContent.layout) — every
 * other section type ignores it. `custom_page`'s catalog depends on which of
 * the four layouts the page is in: `'table'` (and `undefined`, since a
 * `custom_page` with no layout set is a still-coercing `data_table`, which is
 * always a table) reuses the data_table catalog, `'text'` gets its own, and
 * the two media layouts share one (see PAGE_KNOBS above).
 */
export function knobsFor(sectionType: string, layout?: string): readonly KnobDef[] {
  if (sectionType === 'custom_page') {
    if (layout === 'text') return PAGE_KNOBS.custom_page_text!;
    if (layout === 'media_full' || layout === 'media_split') return PAGE_KNOBS.custom_page_media!;
    return PAGE_KNOBS.data_table!;
  }
  return PAGE_KNOBS[sectionType] ?? [];
}

export function knobValue(
  sectionType: string,
  style: SectionStyle | undefined,
  knob: string,
  layout?: string,
): string | undefined {
  const def = knobsFor(sectionType, layout).find((k) => k.knob === knob);
  if (!def) return undefined;
  const raw = style?.[knob];
  return raw !== undefined && def.options.includes(raw) ? raw : def.default;
}

export type StylePatchResult = { ok: true; patch: SectionStyle } | { ok: false; error: string };

/** Validates a style patch against the page type's catalog; unknown knobs or values name the options. */
export function validateStylePatch(sectionType: string, patch: Record<string, unknown>, layout?: string): StylePatchResult {
  const defs = knobsFor(sectionType, layout);
  if (defs.length === 0) return { ok: false, error: `Pages of type "${sectionType}" have no style knobs.` };
  const clean: SectionStyle = {};
  for (const [knob, value] of Object.entries(patch)) {
    const def = defs.find((k) => k.knob === knob);
    if (!def) {
      return { ok: false, error: `Unknown knob "${knob}" for ${sectionType}. Available: ${defs.map((k) => k.knob).join(', ')}.` };
    }
    if (typeof value !== 'string' || !def.options.includes(value)) {
      return { ok: false, error: `"${String(value)}" is not a ${def.label} option. Choose one of ${def.options.join(' / ')}.` };
    }
    clean[knob] = value;
  }
  return { ok: true, patch: clean };
}

/** One step up or down a stepped knob; null at the end of the scale or for choice knobs. */
export function stepKnob(def: KnobDef, current: string, direction: 'up' | 'down'): string | null {
  if (!def.stepped) return null;
  const index = def.options.indexOf(current);
  if (index < 0) return null;
  const next = direction === 'up' ? index + 1 : index - 1;
  return next >= 0 && next < def.options.length ? def.options[next]! : null;
}
